import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnDestroy,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'

import { combineLatest } from 'rxjs'

import { FlxThemeModule } from '@flowx/angular-theme'
import {
  ChatAttachmentEntry,
  ChatConfig,
  ChatHeaderDisplayMode,
  ChatMessage,
  type ChatMessageActionsConfig,
  ChatMessageFile,
  FlxChatComponent,
  FlxIconModule,
  FlxSpinnerComponent,
} from '@flowx/angular-ui-toolkit'
import {
  type ChatFeedbackValue,
  type ChatMessageContent,
  ChatMessageType,
  type ChatRendererAnalytics,
  type ChatSessionState,
  ChatSourceType,
  type ConversationsListPaginationState,
  type MessageTimeGroupLabels,
  type PendingVoiceMessage,
  type SessionTimeGroup,
  type Suggestion,
  activeChatSessionIdStore,
  buildMessageTimeGroupLabels,
  chatRendererAnalyticsConfigs,
  chatStore,
  conversationsListPaginationStore,
  detachChat,
  fetchDocument,
  getLastAgentSuggestions,
  groupSessionsByDate,
  initChat,
  initChatSession,
  loadChatSessionHistory,
  loadConversationsList,
  loadMoreConversations,
  localizeString,
  resolveMessageActionsConfig,
  setActiveChatSessionId,
  theme,
  updateUrlWithQueryParam,
} from '@flowx/core-sdk'
import { ThemeComponent, getComponentStyles } from '@flowx/core-theme'

import { getThemeIcon } from '../components/helpers'
import { FlxLocalizePipe } from '../pipes/flx-localize.pipe'
import { handleAngularAttachments } from '../utils/attachments-upload.utils'
import { createChatAnalytics } from '../utils/chat-analytics.utils'
import {
  createChatMessageFeedback,
  getChatFeedbackClassNames,
} from '../utils/chat-message-feedback.utils'
import { createConversationsSearch } from '../utils/conversations-search.utils'
import { resolveConnectionInputs } from '../utils/deprecated-inputs.utils'
import {
  checkAndClearAngularPendingVoice,
  cleanupAudioUrlCache,
  handleAngularVoiceMessage,
  mergeAngularDisplayMessages,
  normalizeChatHistoryMessage,
  retryAngularVoice,
} from '../utils/voice-upload.utils'

const chatHeaderToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatHeaderFloating,
  fullscreen: ThemeComponent.ChatHeaderFullScreen,
  fill: ThemeComponent.ChatHeader,
}

const chatAgentMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatAgentMessageFloating,
  fullscreen: ThemeComponent.ChatAgentMessageFullScreen,
  fill: ThemeComponent.ChatAgentMessage,
}

const chatInputAreaToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputAreaFloating,
  fullscreen: ThemeComponent.ChatInputAreaFullScreen,
  fill: ThemeComponent.ChatInputArea,
}

const chatErrorMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatErrorMessageFloating,
  fullscreen: ThemeComponent.ChatErrorMessageFullScreen,
  fill: ThemeComponent.ChatErrorMessage,
}

const chatVoiceInputToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatVoiceInputFloating,
  fullscreen: ThemeComponent.ChatVoiceInputFullScreen,
  fill: ThemeComponent.ChatVoiceInput,
}

const chatUserVoiceMessageToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserVoiceMessageFloating,
  fullscreen: ThemeComponent.ChatUserVoiceMessageFullScreen,
  fill: ThemeComponent.ChatUserVoiceMessage,
}

const chatInputFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatInputFileItemFloating,
  fullscreen: ThemeComponent.ChatInputFileItemFullScreen,
  fill: ThemeComponent.ChatInputFileItem,
}

const chatUserFileItemToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatUserFileItemFloating,
  fullscreen: ThemeComponent.ChatUserFileItemFullScreen,
  fill: ThemeComponent.ChatUserFileItem,
}

const chatMessageActionsToComponentType: Record<ChatHeaderDisplayMode, ThemeComponent> = {
  floating: ThemeComponent.ChatMessageActionsFloating,
  fullscreen: ThemeComponent.ChatMessageActionsFullScreen,
  fill: ThemeComponent.ChatMessageActions,
}

/**
 * Attachment limits, used when `showFileUpload` is set. Every field is optional — omit
 * `attachmentsConfig` entirely for the defaults, or pass only the ones you want to
 * change. `maxFilesSize` is the combined size of a selection, matching the chat
 * input's own property.
 */
export interface AttachmentsConfig {
  accept?: string
  maxFiles?: number
  maxFilesSize?: number
}

export interface MessageActionsInput {
  enabled?: boolean
  showTimestamp?: boolean
  showCopyAction?: boolean
  showResponseFeedback?: boolean
  feedbackReasons?: string[]
  dataTestId?: string
}

export type { ChatRendererAnalytics }

@Component({
  selector: 'flx-chat-renderer',
  imports: [FlxChatComponent, FlxSpinnerComponent, FlxThemeModule, FlxIconModule, FlxLocalizePipe],
  template: `@if (chatLoaded()) {
      <flx-chat
        [messages]="displayMessages()"
        [reasoning]="reasoning()"
        [chatConfig]="chatConfig()"
        [showSuggestedPrompts]="showSuggestedPrompts()"
        [agentSuggestions]="agentSuggestions()"
        [showVoiceInput]="showVoiceInput()"
        [showFileUpload]="showFileUpload()"
        [accept]="fileAccept()"
        [maxFiles]="fileMaxFiles()"
        [maxFilesSize]="fileMaxFilesSize()"
        [invalidFileTypeError]="fileInvalidTypeError"
        [filesExceededError]="fileCountExceededError"
        [filesSizeExceededError]="fileSizeExceededError"
        [uploadError]="attachmentsUploadError()"
        [attachIcon]="attachIcon()"
        [deleteIcon]="deleteIcon()"
        [downloadIcon]="downloadIcon()"
        [downloadFile]="downloadAttachment"
        [attachmentsLabels]="attachmentsLabels"
        [inputDisabled]="inputDisabled()"
        [inputDisabledTooltip]="inputDisabledTooltip"
        [fetchAudio]="fetchAudioBlob"
        [voiceInputIcons]="chatIcons().voiceInputIcons"
        [voiceMessageIcons]="chatIcons().voiceMessageIcons"
        [voiceLabels]="voiceLabels"
        [activeView]="activeView()"
        [groupedChatSessions]="groupedChatSessions()"
        [emptyStateText]="emptyStateText()"
        [locale]="locale()"
        [messageActionsConfig]="messageActionsConfig()"
        [messageTimeGroupLabels]="messageTimeGroupLabels"
        [messageActionsCopyIcon]="chatIcons().messageActionsCopyIcon"
        [messageActionsCopySuccessIcon]="chatIcons().messageActionsCopySuccessIcon"
        [messageActionsThumbUpIcon]="chatIcons().messageActionsThumbUpIcon"
        [messageActionsThumbUpSelectedIcon]="chatIcons().messageActionsThumbUpSelectedIcon"
        [messageActionsThumbDownIcon]="chatIcons().messageActionsThumbDownIcon"
        [messageActionsThumbDownSelectedIcon]="chatIcons().messageActionsThumbDownSelectedIcon"
        [feedbackTarget]="messageFeedback.feedbackTarget()"
        [feedbackModalClassName]="feedbackClassNames().modal"
        [feedbackModalHeaderClassName]="feedbackClassNames().modalHeader"
        [feedbackBodyClassName]="feedbackClassNames().body"
        [feedbackTextAreaClassName]="feedbackClassNames().textArea"
        [feedbackErrorClassName]="feedbackClassNames().error"
        [feedbackButtonClassName]="feedbackClassNames().button"
        [tooltipClassName]="helperTooltipClassName()"
        [chatAgentAvatar]="agentAvatarPath()"
        [chatErrorIcon]="errorIconPath()"
        [newChatIcon]="{
          path: chatIcons().newChatIcon,
          ariaLabel: ARIA_LABELS.newChat | flxLocalize,
        }"
        [historyIcon]="
          chatIcons().historyIcon
            ? {
                path: chatIcons().historyIcon,
                ariaLabel: ARIA_LABELS.history | flxLocalize,
              }
            : null
        "
        [backIcon]="
          chatIcons().backIcon
            ? {
                path: chatIcons().backIcon,
              }
            : null
        "
        [newChatTooltip]="headerTooltips.newChat"
        [historyTooltip]="headerTooltips.history"
        [backTooltip]="headerTooltips.back"
        [stopResponse]="handleStopResponse"
        [sendAriaLabel]="inputActionLabels.sendAriaLabel"
        [sendTooltip]="inputActionLabels.sendTooltip"
        [stopAriaLabel]="inputActionLabels.stopAriaLabel"
        [stopTooltip]="inputActionLabels.stopTooltip"
        [hasMoreMessages]="hasMoreMessages()"
        [loadingMore]="loadingMore()"
        [hasMoreSessions]="historyHasMoreSessions()"
        [loadingMoreSessions]="historyLoadingMoreSessions()"
        [showHistorySearch]="true"
        [searchValue]="conversationsSearch.searchText()"
        [searchPlaceholder]="searchLabels.placeholder"
        [searchClearAriaLabel]="searchLabels.clearAriaLabel"
        [searchIconPath]="searchIcons().searchIcon"
        [searchClearIconPath]="searchIcons().clearIcon"
        [searchHighlightText]="conversationsSearch.appliedQuery()"
        (feedbackChange)="handleFeedbackChange($event)"
        (feedbackSubmit)="messageFeedback.handleFeedbackSubmit($event)"
        (feedbackRemove)="messageFeedback.handleFeedbackRemove()"
        (feedbackCancel)="messageFeedback.handleFeedbackCancel()"
        (messageCopied)="chatAnalytics.messageCopied()"
        (searchChange)="conversationsSearch.onSearchChange($event)"
        (loadMore)="handleLoadMore()"
        (sendMessage)="handleTextMessage($event)"
        (sendSuggestedPrompt)="handleSendSuggestedPrompt($event)"
        (promptSelected)="chatAnalytics.configuredPromptSelected($event.label)"
        (voiceMessage)="handleVoiceMessage($event)"
        (attachmentsMessage)="handleAttachmentsMessage($event)"
        (newChat)="handleNewChat()"
        (history)="handleOpenHistory()"
        (backFromHistory)="handleCloseHistory()"
        (selectSession)="handleSelectSession($event)"
        (loadMoreSessions)="handleLoadMoreSessions()"
        [(attachments)]="attachmentEntries"
      >
        <flx-icon
          [path]="chatIcons().voiceMicIcon"
          flxVoiceMicIcon
          aria-hidden="true"
        />
        <flx-icon
          [path]="chatIcons().stopIcon"
          flxChatStopIcon
          aria-hidden="true"
        />
        <flx-icon
          [path]="chatIcons().sendIcon"
          flxChatInputIcon
          aria-hidden="true"
        />
      </flx-chat>
    } @else {
      <flx-spinner
        flxSpinner
        visualType="custom"
      />
    }`,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: `
    .flx-chat {
      height: 100%;
    }
  `,
})
export class FlxChatRendererComponent implements OnInit, OnDestroy {
  #destroyRef = inject(DestroyRef)
  #audioUrlCache = new Map<string, string>()
  // Locally-stored timestamps for messages the backend doesn't stamp; keyed by
  // message id so the value stays stable across stream re-emits.
  #messageTimestamps = new Map<string, Date>()
  #displayMode = computed<ChatHeaderDisplayMode>(() => this.chatConfig()?.displayMode ?? 'floating')

  readonly #sessionGroupLabels: Record<SessionTimeGroup, string> = {
    sys_chat_time_group_today: localizeString('@@sys_chat_time_group_today'),
    sys_chat_time_group_yesterday: localizeString('@@sys_chat_time_group_yesterday'),
    sys_chat_time_group_previous_7_days: localizeString('@@sys_chat_time_group_previous_7_days'),
    sys_chat_time_group_previous_30_days: localizeString('@@sys_chat_time_group_previous_30_days'),
    sys_chat_time_group_older: localizeString('@@sys_chat_time_group_older'),
  }

  readonly inputDisabledTooltip = localizeString('@@sys_chat_disabled_input')

  readonly attachmentsLabels = {
    attachAriaLabel: localizeString('@@sys_a11y_chat_attach_files'),
    removeAriaLabel: localizeString('@@sys_a11y_chat_remove_file'),
    downloadAriaLabel: localizeString('@@sys_a11y_chat_download_file'),
  }

  accessToken = input<string>()
  /** @deprecated Use `accessToken` instead. Kept for backward compatibility; removal will be announced. */
  authToken = input<string>()
  baseUrl = input<string>()
  /** @deprecated Use `baseUrl` instead. Kept for backward compatibility; removal will be announced. */
  apiUrl = input<string>()
  assetsBaseUrl = input<string>()
  /** @deprecated Use `assetsBaseUrl` instead. Kept for backward compatibility; removal will be announced. */
  staticAssetsPath = input<string>()

  enginePath = input<string>()
  /** @deprecated Use `enginePath` instead. Kept for backward compatibility; removal will be announced. */
  processApiPath = input<string>()
  language = input.required<string>()
  locale = input.required<string>()

  themeId = input.required<string>()
  projectId = input.required<string>()
  workspaceId = input.required<string>()

  organizationId = input<string>()
  buildId = input<string>()
  // Set this instead of buildId to resolve settings and resources by application
  // version. The two are different ids and the resource APIs route on which one they
  // are given, so passing a version as buildId sends them to the build route with a
  // value it cannot resolve.
  appVersionId = input<string>()
  runOnAppVersion = input<boolean>(false)
  cache = input<boolean>(true)

  source = input.required<{
    type: ChatSourceType
    id: string
    resourceId?: string
  }>()

  showVoiceInput = input<boolean>(true)
  showFileUpload = input<boolean>(false)
  inputDisabled = input<boolean>(false)

  chatConfig = input.required<ChatConfig>()

  messageActions = input<MessageActionsInput | null>(null)
  attachmentsConfig = input<AttachmentsConfig>({})
  analytics = input<ChatRendererAnalytics | null>(null)

  chatLoaded = signal<boolean>(false)

  messages = signal<ChatMessage[]>([])
  reasoning = signal<boolean>(false)
  showSuggestedPrompts = signal<boolean>(true)
  agentSuggestions = signal<Suggestion[]>([])
  hasMoreMessages = signal<boolean>(false)
  loadingMore = signal<boolean>(false)

  activeView = signal<'chat' | 'history'>('chat')
  hasMoreSessions = signal<boolean>(true)
  loadingMoreSessions = signal<boolean>(false)

  // Voice state
  pendingVoice = signal<PendingVoiceMessage | null>(null)

  // Attachments state
  attachmentEntries = signal<ChatAttachmentEntry[]>([])
  attachmentsUploadError = signal<string | null>(null)

  chatSessions = signal<
    {
      sessionId: string
      summary: string
      messageCount: number
      active: boolean
      createdAt: string
    }[]
  >([])

  displayMessages = computed(() =>
    mergeAngularDisplayMessages(this.messages(), this.pendingVoice(), () => this.handleRetryVoice())
  )

  readonly headerTooltips = {
    newChat: localizeString('@@sys_chat_tooltip_new_chat'),
    history: localizeString('@@sys_chat_tooltip_chat_history'),
    back: localizeString('@@sys_chat_tooltip_back'),
  }

  readonly messageTimeGroupLabels: MessageTimeGroupLabels =
    buildMessageTimeGroupLabels(localizeString)

  fileAccept = computed(() => this.attachmentsConfig().accept ?? '*/*')
  fileMaxFiles = computed(() => this.attachmentsConfig().maxFiles ?? 10)
  fileMaxFilesSize = computed(() => this.attachmentsConfig().maxFilesSize ?? 5000000)

  readonly fileInvalidTypeError = localizeString('@@sys_chat_attachments_invalid_type')
  readonly fileCountExceededError = localizeString('@@sys_chat_attachments_files_exceeded')
  readonly fileSizeExceededError = localizeString('@@sys_chat_attachments_size_exceeded')

  attachIcon = computed(() => ({
    name: 'attach',
    path: this.chatIcons().attachFileIcon,
    ariaLabel: this.attachmentsLabels.attachAriaLabel,
  }))
  deleteIcon = computed(() => ({
    name: 'close',
    path: this.chatIcons().fileDeleteIcon,
    ariaLabel: this.attachmentsLabels.removeAriaLabel,
  }))
  downloadIcon = computed(() => ({
    name: 'download',
    path: this.chatIcons().fileDownloadIcon,
    ariaLabel: this.attachmentsLabels.downloadAriaLabel,
  }))

  messageActionsConfig = computed<ChatMessageActionsConfig | null>(() => {
    const ma = this.messageActions()
    if (ma?.enabled === false) {
      return null
    }
    return resolveMessageActionsConfig(ma, localizeString)
  })

  readonly ARIA_LABELS = {
    newChat: '@@sys_a11y_chat_new',
    history: '@@sys_a11y_chat_history',
  }

  readonly voiceLabels = {
    listeningLabel: localizeString('@@sys_chat_voice_input_recording'),
    voiceMessageLabel: localizeString('@@sys_chat_voice_input_message'),
    cancelAriaLabel: localizeString('@@sys_a11y_chat_voice_input_cancel'),
    confirmAriaLabel: localizeString('@@sys_a11y_chat_voice_input_confirm'),
    deleteAriaLabel: localizeString('@@sys_a11y_chat_voice_input_delete'),
    inputPlayAriaLabel: localizeString('@@sys_a11y_chat_voice_input_play'),
    inputPauseAriaLabel: localizeString('@@sys_a11y_chat_voice_input_pause'),
    sendingLabel: localizeString('@@sys_chat_voice_message_sending'),
    messageLabel: localizeString('@@sys_chat_voice_message'),
    errorLabel: localizeString('@@sys_chat_voice_message_error'),
    messagePlayAriaLabel: localizeString('@@sys_a11y_chat_voice_message_play'),
    messagePauseAriaLabel: localizeString('@@sys_a11y_chat_voice_message_pause'),
    retryAriaLabel: localizeString('@@sys_a11y_chat_voice_message_retry'),
    progressAriaLabel: localizeString('@@sys_a11y_chat_voice_message_progress'),
  }

  readonly inputActionLabels = {
    sendAriaLabel: localizeString('@@sys_a11y_chat_submit_message'),
    sendTooltip: localizeString('@@sys_chat_submit_message'),
    stopAriaLabel: localizeString('@@sys_a11y_chat_cancel_request'),
    stopTooltip: localizeString('@@sys_chat_cancel_request'),
  }

  readonly CHAT_VOICE_INPUT_ICON_ARIA_LABELS = {
    cancel: localizeString('@@sys_a11y_chat_voice_input_cancel'),
    confirm: localizeString('@@sys_a11y_chat_voice_input_confirm'),
    delete: localizeString('@@sys_a11y_chat_voice_input_delete'),
    play: localizeString('@@sys_a11y_chat_voice_input_play'),
    pause: localizeString('@@sys_a11y_chat_voice_input_pause'),
  }

  conversationsSearch = createConversationsSearch(() => this.source().id, this.#destroyRef)

  readonly searchLabels = {
    placeholder: localizeString('@@sys_chat_conversations_search_placeholder'),
    clearAriaLabel: localizeString('@@sys_a11y_conversations_search_clear'),
    noResults: localizeString('@@sys_chat_conversations_no_results'),
  }

  // While the first search page loads, suppress the no-results text.
  emptyStateText = computed(() =>
    this.conversationsSearch.isSearchActive()
      ? this.conversationsSearch.searchLoading()
        ? ''
        : this.searchLabels.noResults
      : localizeString('@@sys_chat_conversations_empty_state')
  )

  groupedChatSessions = computed(() =>
    groupSessionsByDate(
      this.conversationsSearch.isSearchActive()
        ? this.conversationsSearch.searchSessions()
        : this.chatSessions(),
      this.#sessionGroupLabels
    )
  )

  // Search results are unpaginated — scroll never loads more while searching.
  historyHasMoreSessions = computed(() =>
    this.conversationsSearch.isSearchActive() ? false : this.hasMoreSessions()
  )
  historyLoadingMoreSessions = computed(() =>
    this.conversationsSearch.isSearchActive()
      ? this.conversationsSearch.searchLoading()
      : this.loadingMoreSessions()
  )

  searchIcons = computed(() => ({
    searchIcon: getThemeIcon(ThemeComponent.ChatHistorySearch, 'searchIcon'),
    clearIcon: getThemeIcon(ThemeComponent.ChatHistorySearch, 'clearIcon'),
  }))

  chatIcons = computed(() => {
    const mode = this.#displayMode()
    return {
      newChatIcon: getThemeIcon(chatHeaderToComponentType[mode], 'newChatIcon'),
      historyIcon: getThemeIcon(chatHeaderToComponentType[mode], 'historyIcon'),
      backIcon: getThemeIcon(chatHeaderToComponentType[mode], 'backIcon'),
      sendIcon: getThemeIcon(chatInputAreaToComponentType[mode], 'sendMessageIcon'),
      stopIcon: getThemeIcon(chatInputAreaToComponentType[mode], 'stopResponseIcon'),
      voiceMicIcon: getThemeIcon(chatInputAreaToComponentType[mode], 'voiceInputIcon'),
      voiceInputIcons: {
        cancelIcon: {
          path: getThemeIcon(chatVoiceInputToComponentType[mode], 'cancelIcon'),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.cancel),
        },
        confirmIcon: {
          path: getThemeIcon(chatVoiceInputToComponentType[mode], 'confirmIcon'),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.confirm),
        },
        deleteIcon: {
          path: getThemeIcon(chatVoiceInputToComponentType[mode], 'deleteIcon'),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.delete),
        },
        playIcon: {
          path: getThemeIcon(chatVoiceInputToComponentType[mode], 'playIcon'),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.play),
        },
        pauseIcon: {
          path: getThemeIcon(chatVoiceInputToComponentType[mode], 'pauseIcon'),
          ariaLabel: localizeString(this.CHAT_VOICE_INPUT_ICON_ARIA_LABELS.pause),
        },
      },
      voiceMessageIcons: {
        playIcon: getThemeIcon(chatUserVoiceMessageToComponentType[mode], 'playIcon'),
        pauseIcon: getThemeIcon(chatUserVoiceMessageToComponentType[mode], 'pauseIcon'),
        retryIcon: getThemeIcon(chatUserVoiceMessageToComponentType[mode], 'retryIcon'),
      },
      attachFileIcon: getThemeIcon(chatInputAreaToComponentType[mode], 'attachFileIcon'),
      fileDeleteIcon: getThemeIcon(chatInputFileItemToComponentType[mode], 'deleteIcon'),
      fileDownloadIcon: getThemeIcon(chatUserFileItemToComponentType[mode], 'downloadIcon'),
      messageActionsCopyIcon: getThemeIcon(chatMessageActionsToComponentType[mode], 'copyIcon'),
      messageActionsCopySuccessIcon: getThemeIcon(
        chatMessageActionsToComponentType[mode],
        'copySuccessIcon'
      ),
      messageActionsThumbUpIcon: getThemeIcon(
        chatMessageActionsToComponentType[mode],
        'thumbUpIcon'
      ),
      messageActionsThumbUpSelectedIcon: getThemeIcon(
        chatMessageActionsToComponentType[mode],
        'thumbUpSelectedIcon'
      ),
      messageActionsThumbDownIcon: getThemeIcon(
        chatMessageActionsToComponentType[mode],
        'thumbDownIcon'
      ),
      messageActionsThumbDownSelectedIcon: getThemeIcon(
        chatMessageActionsToComponentType[mode],
        'thumbDownSelectedIcon'
      ),
    }
  })

  feedbackClassNames = computed(() => getChatFeedbackClassNames(this.#displayMode()))

  // This stack has no template config: build one per chat component from the inputs.
  chatAnalytics = createChatAnalytics(() =>
    chatRendererAnalyticsConfigs({
      analytics: this.analytics(),
      suggestedPrompts: this.chatConfig()?.suggestedPrompts,
      feedbackReasons: this.messageActions()?.feedbackReasons,
    })
  )

  messageFeedback = createChatMessageFeedback(
    (value, reason) => this.chatAnalytics.feedbackSent(value, reason),
    (removedValue) => this.chatAnalytics.feedbackUnset(removedValue)
  )

  agentAvatarPath = computed(() =>
    getThemeIcon(chatAgentMessageToComponentType[this.#displayMode()], 'agentIcon')
  )

  errorIconPath = computed(() =>
    getThemeIcon(chatErrorMessageToComponentType[this.#displayMode()], 'errorIcon')
  )

  helperTooltipClassName = computed(
    () => `${getComponentStyles(ThemeComponent.HelperTooltip, theme()) ?? ''} flx-themed`
  )

  loadMoreMessages: (() => Promise<void>) | null = null
  handleStopResponse: (() => Promise<void>) | null = null
  handleSendMessage: (message: string, messageContent?: ChatMessageContent) => void
  handleSendSuggestedPrompt = (prompt: { label: string; prompt: string }): void => {
    this.handleSendMessage(prompt.label, {
      type: ChatMessageType.SUGGESTED_PROMPT,
      label: prompt.label,
      prompt: prompt.prompt || '',
    })
    this.chatAnalytics.agentPromptSelected()
  }

  handleTextMessage(message: string): void {
    this.handleSendMessage(message)
    this.chatAnalytics.textMessageSent()
  }
  fetchAudioBlob = (url: string): Promise<Blob | null> => fetchDocument(url)

  downloadAttachment = async (file: ChatMessageFile): Promise<void> => {
    if (!file.downloadUrl) {
      return
    }
    const blob = await fetchDocument(file.downloadUrl)
    if (!blob) {
      return
    }
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = file.name
    anchor.click()
    URL.revokeObjectURL(url)
  }

  ngOnInit(): void {
    // Subscribe to chat store for session history
    combineLatest([chatStore.stream$, activeChatSessionIdStore.stream$])
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(([store, currentId]: [Record<string, ChatSessionState>, string | null]) => {
        this.chatSessions.set(
          Object.entries(store)
            .filter(([, session]) => !session.isLocalOnly && session.isSessionUpdated)
            .map(([sessionId, session]) => ({
              sessionId,
              summary: session.summary,
              messageCount: session.messageCount,
              active: sessionId === currentId,
              createdAt: session.createdAt,
            }))
        )
      })

    // Subscribe to conversations list pagination state
    conversationsListPaginationStore.stream$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((pagination: ConversationsListPaginationState) => {
        this.hasMoreSessions.set(pagination.hasMore)
        this.loadingMoreSessions.set(pagination.loading)
      })

    initChat({
      ...resolveConnectionInputs(this),
      language: this.language(),
      locale: this.locale(),
      themeId: this.themeId(),
      workspaceId: this.workspaceId(),
      buildId: this.buildId(),
      source: this.source(),
      organizationId: this.organizationId(),
      appInfo: {
        appId: this.projectId(),
        appVersionId: this.appVersionId() ?? this.buildId(),
      },
      cache: this.cache(),
      runOnAppVersion: this.runOnAppVersion(),
    }).then(({ sendMessage, chat$, loadMoreMessages, stopResponse }) => {
      this.chatLoaded.set(true)
      this.handleSendMessage = sendMessage
      this.loadMoreMessages = loadMoreMessages
      this.handleStopResponse = stopResponse
      chat$
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe(({ chatHistory, loading, hasMoreMessages, loadingMore, isLocalOnly }) => {
          this.hasMoreMessages.set(hasMoreMessages ?? false)
          this.loadingMore.set(loadingMore ?? false)
          this.reasoning.set(loading)
          this.showSuggestedPrompts.set(isLocalOnly)
          if (isLocalOnly) {
            this.agentSuggestions.set([])
            this.messages.set([
              {
                id: '1',
                content: this.chatConfig()?.welcomeMessage || 'Welcome to the chat!',
                sender: 'receiver',
                timestamp: new Date(),
                isWelcome: true,
              },
            ])
          } else {
            this.agentSuggestions.set(getLastAgentSuggestions(chatHistory ?? []))
            const mapped = chatHistory?.map((item) => normalizeChatHistoryMessage(item, new Map()))
            checkAndClearAngularPendingVoice(mapped, this.pendingVoice)
            this.messages.set(
              chatHistory?.map((item) =>
                normalizeChatHistoryMessage(item, this.#audioUrlCache, this.#messageTimestamps)
              )
            )
          }
        })
    })
  }

  ngOnDestroy(): void {
    detachChat()
    cleanupAudioUrlCache(this.#audioUrlCache)
  }

  handleVoiceMessage(event: { blob: Blob; duration: number }): void {
    this.reasoning.set(true)
    handleAngularVoiceMessage(
      event,
      this.pendingVoice,
      this.#audioUrlCache,
      (msg, content) => this.#sendVoiceMessage(msg, content),
      () => this.reasoning.set(false)
    )
  }

  handleAttachmentsMessage(event: { message: string; files: File[] }): void {
    // `reasoning` is deliberately not toggled here: the send button is already
    // disabled by the input's own in-flight guard, and the thinking indicator is
    // driven by the chat stream's loading flag, same as a text message.
    handleAngularAttachments(
      event,
      this.attachmentsUploadError,
      (msg, content) => {
        this.handleSendMessage(msg, content)
        this.chatAnalytics.textMessageSent()
      },
      (success) => {
        if (success) {
          this.attachmentEntries.set([])
        }
      }
    )
  }

  handleRetryVoice(): void {
    this.reasoning.set(true)
    retryAngularVoice(
      this.pendingVoice,
      this.#audioUrlCache,
      (msg, content) => this.#sendVoiceMessage(msg, content),
      () => this.reasoning.set(false)
    )
  }

  handleNewChat(): void {
    initChatSession()
    updateUrlWithQueryParam('chatSessionId', null)
    this.#messageTimestamps.clear()

    // Reset messages, reasoning, and switch to chat view
    const chatConfig = this.chatConfig()
    const welcomeMessage = chatConfig?.welcomeMessage
    this.messages.set(
      welcomeMessage
        ? [
            {
              id: 'welcome-message',
              content: welcomeMessage,
              sender: 'receiver',
              timestamp: new Date(),
              isWelcome: true,
            },
          ]
        : []
    )
    this.reasoning.set(false)
    this.agentSuggestions.set([])
    this.pendingVoice.set(null)
    this.activeView.set('chat')
  }

  async handleOpenHistory(): Promise<void> {
    try {
      await loadConversationsList(this.source().id)
    } catch (error) {
      console.error('Failed to load conversations list', error)
    }
    this.activeView.set('history')
  }

  async handleLoadMoreSessions(): Promise<void> {
    await loadMoreConversations(this.source().id)
  }

  handleCloseHistory(): void {
    this.conversationsSearch.clearSearch()
    this.activeView.set('chat')
  }

  handleSelectSession(sessionId: string): void {
    setActiveChatSessionId(sessionId)
    updateUrlWithQueryParam('chatSessionId', sessionId)
    loadChatSessionHistory(sessionId)
    this.conversationsSearch.clearSearch()
    this.activeView.set('chat')
  }

  handleFeedbackChange({
    messageId,
    value,
  }: {
    messageId: string
    value: ChatFeedbackValue | null
  }): void {
    this.messageFeedback.handleFeedbackChange(
      messageId,
      value,
      this.messages().find(
        (message) => message.messageId === messageId && message.sender === 'receiver'
      )?.feedback ?? null
    )
  }

  handleLoadMore(): void {
    if (!this.hasMoreMessages() || this.loadingMore()) {
      return
    }
    this.loadMoreMessages?.()
  }

  // Called once the recording is uploaded, so a failed upload sends no event until a retry succeeds.
  #sendVoiceMessage(message: string, content?: ChatMessageContent): void {
    this.handleSendMessage(message, content)
    this.chatAnalytics.voiceMessageSent()
  }
}
